import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { BrandHeader } from "@/components/BrandHeader";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const formatEur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

export default function PagarRestoPage() {
  const [sp] = useSearchParams();
  const regId = sp.get("reg") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!regId) { setLoading(false); return; }
    api.get(`/public/balance/${regId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [regId]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data: co } = await api.post("/public/balance-checkout", { reg_id: regId, origin_url: window.location.origin });
      window.location.href = co.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo iniciar el pago");
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]"><BrandHeader />
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white rounded-3xl shadow-lg p-8">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-[#003284]" size={32} /></div>
          ) : !data ? (
            <div className="text-center space-y-2"><h1 className="text-2xl font-black brand-navy">Enlace no válido</h1><p className="text-slate-500">No hemos encontrado esta inscripción.</p></div>
          ) : data.balance_status === "paid" ? (
            <div className="text-center space-y-3" data-testid="balance-paid">
              <CheckCircle2 className="mx-auto text-emerald-500" size={56} />
              <h1 className="text-2xl font-black brand-navy">Ya está pagado</h1>
              <p className="text-slate-500">El resto de tu inscripción a <strong>{data.activity_title}</strong> ya consta como pagado. ¡Gracias!</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-black brand-navy text-center">Pagar el resto</h1>
              <p className="text-center text-slate-500 mt-2 mb-5">{data.activity_title} · Ref. {data.reference}</p>
              <div className="rounded-2xl bg-[#F8FAFC] border p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Reserva ya pagada</span><span className="font-semibold">{formatEur(data.deposit_amount)}</span></div>
                <div className="flex justify-between text-lg"><span className="font-black brand-navy">Importe pendiente</span><span data-testid="balance-amount" className="font-black text-[#FF7F27]">{formatEur(data.balance_amount)}</span></div>
                {data.balance_due_date && <p className="text-xs text-slate-500">Fecha límite: <strong>{data.balance_due_date}</strong></p>}
                <p className="text-xs text-slate-400">La reserva es {data.deposit_refundable ? "reembolsable según las condiciones de cancelación" : "no reembolsable"}.</p>
              </div>
              <button data-testid="balance-pay-btn" disabled={paying} onClick={pay} className="mt-5 w-full bg-brand-orange text-white font-black text-lg rounded-full py-4 shadow-lg hover:bg-[#DE7A1C] disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                {paying ? <Loader2 className="animate-spin" size={20} /> : <><ShieldCheck size={20} /> Pagar {formatEur(data.balance_amount)}</>}
              </button>
              <p className="text-[11px] text-center text-slate-400 mt-2">Pago seguro con Stripe</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
